import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';

import CommonSelect from '../../../form-components/CommonSelect';
import CommonPagination from '../../../form-components/CommonPagination';
import { getContracts, getContractPdf, getContractCategories, getContractStatuses } from '../../../api/contract';
import {
  ToolbarRow,
  PageSizeGroup,
  PageSizeLabel,
  SearchField,
  SearchInput,
  ActionGroup,
  ActionButton,
  Table,
  TableHeader,
  TableRow,
  Cell,
  HeaderText,
  CellDivider,
  CheckBoxCell,
  DateText,
  NameText,
  CategoryText,
  RegisterText,
  PhoneText,
  DetailButton,
  StatusText,
} from './ElectronicContractStyled';

const COLUMNS = [
  { key: 'contractDate', label: '계약일', width: 160 },
  { key: 'name', label: '계약서 명', width: 280 },
  { key: 'category', label: '카테고리' },
  { key: 'registrationType', label: '등록유형' },
  { key: 'contractor', label: '계약자' },
  { key: 'phone', label: '전화번호' },
  { key: 'detail', label: '상세보기' },
  { key: 'status', label: '상태' },
];

const PAGE_SIZE_OPTIONS = [
  { value: 10, label: '10' },
  { value: 20, label: '20' },
  { value: 50, label: '50' },
];

// { value, label }[] 배열에서 value로 label 찾기
const findLabel = (list, value) => list.find(item => item.value === value)?.label || value;

const ElectronicContractList = ({ branchId, onDetail }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const token = useSelector(state => state.user.user?.token);

  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [category, setCategory] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  const buildQuery = () => {
    const params = new URLSearchParams({ page, limit: pageSize, sortOrder: 'DESCENDING' });
    if (branchId) params.set('branchId', branchId);
    if (appliedKeyword) params.set('keyword', appliedKeyword);
    if (category) params.set('category', category);
    return params.toString();
  };

  const { data } = useQuery({
    queryKey: ['contracts', branchId, appliedKeyword, category, page, pageSize],
    queryFn: () => getContracts(token, buildQuery()),
    enabled: !!token && !!branchId,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['contractCategories'],
    queryFn: () => getContractCategories(token),
    enabled: !!token,
    staleTime: 1000 * 60 * 30,
  });

  const { data: statuses = [] } = useQuery({
    queryKey: ['contractStatuses'],
    queryFn: () => getContractStatuses(token),
    enabled: !!token,
    staleTime: 1000 * 60 * 30,
  });

  const rows = data?.content || [];

  const applySearch = () => {
    setPage(1);
    setAppliedKeyword(keyword.trim());
  };

  const handlePageSizeChange = e => {
    setPageSize(e.target.value);
    setPage(1);
  };

  const handleCategoryChange = e => {
    setCategory(e.target.value);
    setPage(1);
  };

  const allChecked = rows.length > 0 && rows.every(row => selectedIds.includes(row.id));
  const toggleSelectAll = () => setSelectedIds(allChecked ? [] : rows.map(row => row.id));
  const toggleSelectOne = id =>
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]));

  // 선택된 계약서의 PDF를 백엔드를 통해 다운로드한다.
  const handleDownloadPdf = async () => {
    if (selectedIds.length === 0) return toast.error(t('PDF를 다운로드할 계약서를 선택해 주세요.'));

    for (const id of selectedIds) {
      const row = rows.find(r => r.id === id);
      try {
        const blob = await getContractPdf(id, token);
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = row?.name ? `${row.name}_${row.memberName || ''}.pdf` : `contract-${id}.pdf`;
        anchor.click();
        window.URL.revokeObjectURL(url);
      } catch {
        toast.error(t('PDF 다운로드에 실패했습니다.'));
      }
    }
  };

  const handleDetail = row => {
    if (onDetail) return onDetail(row.id);
    // onDetail이 없으면 PDF를 새 탭에서 열기 (fallback)
    if (!row.pdfUrl) return toast.error(t('PDF가 아직 생성되지 않았습니다.'));
    window.open(row.pdfUrl, '_blank');
  };

  return (
    <>
      {/* 검색 / 필터 / 액션 영역 */}
      <ToolbarRow>
        <PageSizeGroup>
          <PageSizeLabel>{t('페이지당 항목 수')}</PageSizeLabel>
          <CommonSelect value={pageSize} onChange={handlePageSizeChange} options={PAGE_SIZE_OPTIONS} width={100} />
        </PageSizeGroup>

        <SearchField>
          <SearchInput
            placeholder={t('이름 및 연락처로 검색')}
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && applySearch()}
          />
          <SearchIcon sx={{ color: '#A6A6A6', cursor: 'pointer' }} onClick={applySearch} />
        </SearchField>

        <ActionGroup>
          <CommonSelect
            value={category}
            onChange={handleCategoryChange}
            emptyLabel={t('전체')}
            options={categories}
            width={140}
            sx={{ '& .MuiOutlinedInput-root': { height: '48px' } }}
          />
          <ActionButton tone="danger" startIcon={<PictureAsPdfOutlinedIcon />} onClick={handleDownloadPdf}>
            {t('PDF 다운로드')}
          </ActionButton>
        </ActionGroup>
      </ToolbarRow>

      {/* 표 */}
      <Table>
        <TableHeader>
          <CheckBoxCell checked={allChecked} clickable onClick={toggleSelectAll}>
            {allChecked && <CheckIcon sx={{ fontSize: 14 }} />}
          </CheckBoxCell>
          {COLUMNS.map((col, idx) => (
            <Fragment key={col.key}>
              {idx > 0 && <CellDivider />}
              <Cell width={col.width}>
                <HeaderText>{t(col.label)}</HeaderText>
              </Cell>
            </Fragment>
          ))}
        </TableHeader>

        {rows.length === 0 && (
          <TableRow style={{ justifyContent: 'center', minHeight: '120px', color: '#A6A6A6', fontSize: '14px' }}>
            {t('검색 결과가 없습니다.')}
          </TableRow>
        )}

        {rows.map(row => {
          const isChecked = selectedIds.includes(row.id);
          const isCanceled = row.status === 'CANCELED';
          return (
            <TableRow key={row.id}>
              <CheckBoxCell checked={isChecked} clickable onClick={() => toggleSelectOne(row.id)}>
                {isChecked && <CheckIcon sx={{ fontSize: 14 }} />}
              </CheckBoxCell>
              <Cell width={160}>
                <DateText>{row.registeredDate?.replace('T', ' ')}</DateText>
              </Cell>
              <Cell width={280}>
                <NameText>{row.name}</NameText>
              </Cell>
              <Cell>
                <CategoryText>{findLabel(categories, row.category)}</CategoryText>
              </Cell>
              <Cell>
                <RegisterText>{row.registrationTypeLabel}</RegisterText>
              </Cell>
              <Cell>
                <RegisterText>{row.memberName}</RegisterText>
              </Cell>
              <Cell>
                <PhoneText>{row.phone}</PhoneText>
              </Cell>
              <Cell>
                <DetailButton onClick={() => handleDetail(row)}>{t('상세보기')}</DetailButton>
              </Cell>
              <Cell>
                <StatusText canceled={isCanceled}>
                  {findLabel(statuses, row.status)}
                </StatusText>
              </Cell>
            </TableRow>
          );
        })}
      </Table>

      {/* 페이지네이션 */}
      <CommonPagination
        totalElements={data?.totalElements}
        limit={pageSize}
        page={page}
        onChange={(_, value) => setPage(value)}
      />
    </>
  );
};

export default ElectronicContractList;
